/**
 * Flow graph colours (DEC-188 decision 2). The ONE place these are defined.
 *
 * Graph-specific hues from the approved mock, one per kind of data. They are
 * not theme tokens on purpose: they appear nowhere else in the product, and a
 * token per graph kind would widen the palette for a single view.
 *
 * Light theme: the mock's pastels measure about 1.6:1 to 2.2:1 on white, below
 * WCAG 1.4.11's 3:1 for graphical objects and far below 4.5:1 for the 11px edge
 * labels drawn in the same colour. Light therefore uses deeper variants of the
 * same hues, each measured on #FFFFFF (ratio in the comment). Dark keeps the
 * mock's values exactly (all 7.2:1 or more on #211B31).
 */
import { useEffect, useState } from 'react'

import { currentTheme, subscribe, type Theme } from '../../../theme'
import type { EdgeKind } from './edgeModel'

type DataKind = Exclude<EdgeKind, 'order'>

export const GRAPH_KIND_COLOR: Record<Theme, Record<DataKind, string>> = {
  dark: {
    plan: '#F2A7C3', // file lists
    history: '#E8C77A', // git history
    complex: '#8FA7FF', // complex files
    signal: '#C6B4E8', // shared signal
    map: '#5ED1C1', // service map
    findings: '#4FBF95', // findings
  },
  light: {
    plan: '#C2407A', // 4.87:1
    history: '#9A6B00', // 4.69:1
    complex: '#3F5BD8', // 5.68:1
    signal: '#7351C2', // 5.70:1
    map: '#0E8577', // 4.53:1
    findings: '#1C8159', // 4.85:1
  },
}

export const GRAPH_KIND_LABEL: Record<DataKind, string> = {
  plan: 'File lists',
  history: 'Git history',
  complex: 'Complex files',
  signal: 'Shared signal',
  map: 'Service map',
  findings: 'Findings',
}

export const GRAPH_KIND_ORDER: DataKind[] = ['plan', 'history', 'complex', 'signal', 'map', 'findings']

/** Champagne border: the selected card, Critic and Report. Light: 4.69:1. */
export const GRAPH_CHAMPAGNE: Record<Theme, string> = { dark: '#E8C77A', light: '#9A6B00' }

export interface GraphPalette {
  theme: Theme
  kind: Record<DataKind, string>
  champagne: string
}

export function graphPalette(theme: Theme): GraphPalette {
  return { theme, kind: GRAPH_KIND_COLOR[theme], champagne: GRAPH_CHAMPAGNE[theme] }
}

/** Colour of an edge. Order lines and not-reached lines use theme tokens instead. */
export function edgeColor(kind: EdgeKind, palette: GraphPalette): string {
  return kind === 'order' ? 'var(--edge-idle)' : palette.kind[kind]
}

/** Tracks the active theme so the graph switches palette with the toggle. */
export function useGraphPalette(): GraphPalette {
  const [theme, setTheme] = useState<Theme>(() => currentTheme())
  useEffect(() => subscribe((t) => setTheme(t)), [])
  return graphPalette(theme)
}

/** The running card's breathing glow, two keyframe stops per theme. */
export const GRAPH_GLOW: Record<Theme, { a: string; b: string; c: string; d: string }> = {
  dark: { a: 'rgba(198,180,232,0.55)', b: 'rgba(128,94,197,0.45)', c: 'rgba(232,199,122,0.7)', d: 'rgba(128,94,197,0.75)' },
  light: { a: 'rgba(128,94,197,0.5)', b: 'rgba(128,94,197,0.28)', c: 'rgba(154,107,0,0.6)', d: 'rgba(128,94,197,0.5)' },
}
